// Tracking T0 (Scope App-Tracking 09.10.2026, §1.4 Befund 1 und 2): Dauer von Sitzung und
// Seitenaufruf zuverlässig schreiben.
//
// Bis 09.10. schickte das Sitzungsende einen PATCH mit dem Publishable Key als Bearer. Ohne
// Nutzer-Token gibt es kein auth.uid(), die RLS-Policy „Users can update own sessions“ greift
// nicht, das Update trifft still 0 Zeilen (seit 02/2026 keine Sitzung mit Dauer). Die letzte
// Seite ging per sendBeacon ohne apikey und ohne Token an REST und wurde abgelehnt.
//
// Jetzt: PATCH mit dem Zugangs-Token des eingeloggten Nutzers, `keepalive`, damit er auch beim
// Schließen der Seite noch rausgeht. Geschrieben wird beim Wechsel in den Hintergrund
// (visibilitychange hidden, pagehide), nicht bei beforeunload (feuert in der App nie).
// Gemessen wird Vordergrundzeit: Zeit im Hintergrund zählt nicht.
//
// Reine Funktionen ohne Supabase-Import, damit scripts/check-tracking-abschluss.ts sie prüfen kann.
// Nichts landet auf dem Gerät (§ 25 TDDDG): das Token liegt nur im Arbeitsspeicher.

/** Zählt Vordergrundzeit: läuft nur, solange die Seite sichtbar ist. */
export class Vordergrunduhr {
  private summeMs = 0;
  private seit: number | null;

  constructor(jetzt: number, sichtbar: boolean) {
    this.seit = sichtbar ? jetzt : null;
  }

  /** Seite geht in den Hintergrund. Mehrfacher Aufruf ist harmlos. */
  pause(jetzt: number): void {
    if (this.seit !== null) {
      this.summeMs += Math.max(0, jetzt - this.seit);
      this.seit = null;
    }
  }

  /** Seite ist wieder sichtbar. Mehrfacher Aufruf ist harmlos. */
  weiter(jetzt: number): void {
    if (this.seit === null) this.seit = jetzt;
  }

  /** Vordergrundzeit bis jetzt in ganzen Sekunden. */
  sekunden(jetzt: number): number {
    const laufend = this.seit !== null ? Math.max(0, jetzt - this.seit) : 0;
    return Math.round((this.summeMs + laufend) / 1000);
  }
}

export interface AbschlussEingabe {
  /** VITE_SUPABASE_URL */
  basisUrl: string;
  /** VITE_SUPABASE_PUBLISHABLE_KEY, nur als apikey (Projekt-Kennung), nie als Bearer. */
  apikey: string;
  /** Zugangs-Token des eingeloggten Nutzers. Ohne Token wird nichts gesendet. */
  token: string | null;
  tabelle: "user_sessions" | "page_views";
  /** Spalte und Wert, über die genau eine eigene Zeile getroffen wird. */
  spalte: "session_id" | "id";
  wert: string;
  felder: { ended_at?: string; left_at?: string; duration_seconds: number };
}

/** Baut den PATCH-Aufruf für das Abschließen. null = nicht senden (kein Login, nichts zu schreiben). */
export function abschlussAnfrage(e: AbschlussEingabe): { url: string; init: RequestInit } | null {
  if (!e.token || !e.wert || !e.basisUrl) return null;
  if (!Number.isFinite(e.felder.duration_seconds) || e.felder.duration_seconds < 1) return null;
  return {
    url: `${e.basisUrl}/rest/v1/${e.tabelle}?${e.spalte}=eq.${encodeURIComponent(e.wert)}`,
    init: {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        apikey: e.apikey,
        Authorization: `Bearer ${e.token}`,
        Prefer: "return=minimal",
      },
      body: JSON.stringify(e.felder),
      keepalive: true,
    },
  };
}
